import React, { useState } from "react"
import { Alert, Button, FormFeedback, FormGroup, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { familyName, firstOffer, offeredName, quantityLabel, supplierNames } from "./labels"

// The dialogs of the Products section. Each sends one line command through `onSubmit`; a refusal
// stays in the dialog until the administrator changes something or closes it.
function LineDialog({ title, failure, onClose, actions, children }) {
  return <Modal isOpen centered toggle={onClose}>
    <ModalHeader toggle={onClose}>{title}</ModalHeader>
    <ModalBody>
      {children}
      {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    </ModalBody>
    <ModalFooter><Button color="light" onClick={onClose}>Cancel</Button>{actions}</ModalFooter>
  </Modal>
}

function NotesField({ id, notes, onChange, hint }) {
  return <FormGroup className="mb-0">
    <Label for={id}>Notes</Label>
    <Input id={id} type="textarea" rows={2} maxLength={500} value={notes} onChange={event => onChange(event.target.value)} />
    <FormText>{hint}</FormText>
  </FormGroup>
}

// The quantity set by admin for a line or one of its places, with the line's notes. Clearing it
// brings back the calculated quantity and keeps the notes.
export function QuantityDialog({ name, unit, quantities, notes: savedNotes, pending, failure, onSubmit, onClose }) {
  const { calculated, setByAdmin } = quantities
  const [quantity, setQuantity] = useState(setByAdmin ? String(setByAdmin.quantity) : "")
  const [notes, setNotes] = useState(savedNotes)
  const valid = /^\d+$/.test(quantity) && Number(quantity) <= 1000000
  const changed = quantities.marks.includes("calculationChanged")
  return <LineDialog title={`Quantity set by admin — ${name}`} failure={failure} onClose={onClose} actions={<>
    {setByAdmin && <Button color="light" disabled={pending} onClick={() => onSubmit({ action: "clearQuantity", notes })}>Clear</Button>}
    <Button color="primary" disabled={pending || !valid} onClick={() => onSubmit({ action: "setQuantity", quantity: Number(quantity), notes })}>
      {pending ? "Saving…" : "Save"}
    </Button>
  </>}>
    <p className="mb-2">{calculated == null ? "No calculation rule: enter the quantity to send." : `Calculated: ${quantityLabel(calculated, unit)}`}</p>
    {changed && <Alert color="warning" className="py-2">
      {`The calculation changed from ${quantityLabel(setByAdmin.calculated, unit)} to ${quantityLabel(calculated, unit)} since this quantity was set. `
        + "Save it to keep it, or clear it to use the calculation."}
    </Alert>}
    <FormGroup>
      <Label for="logistics-quantity">Quantity</Label>
      <Input id="logistics-quantity" type="number" min={0} step={1} value={quantity} onChange={event => setQuantity(event.target.value)} />
      <FormText>A whole number; 0 is allowed.</FormText>
    </FormGroup>
    <NotesField id="logistics-quantity-notes" notes={notes} onChange={setNotes} hint="Optional. The notes stay on the line when the quantity is cleared." />
  </LineDialog>
}

export function NotesDialog({ name, notes: savedNotes, pending, failure, onSubmit, onClose }) {
  const [notes, setNotes] = useState(savedNotes)
  return <LineDialog title={`Notes — ${name}`} failure={failure} onClose={onClose}
    actions={<Button color="primary" disabled={pending} onClick={() => onSubmit({ action: "setNotes", notes })}>{pending ? "Saving…" : "Save"}</Button>}>
    <NotesField id="logistics-line-notes" notes={notes} onChange={setNotes} hint="Shown under the product on this sheet. Leave empty to remove them." />
  </LineDialog>
}

// A standard (generic) line is removed only with an explanation; a product added from the catalogue without one.
export function RemoveLineDialog({ name, generic, pending, failure, onSubmit, onClose }) {
  const [reason, setReason] = useState("")
  const [missing, setMissing] = useState(false)
  const remove = () => {
    if (generic && !reason.trim()) return setMissing(true)
    return onSubmit({ action: "removeLine", ...(generic ? { reason: reason.trim() } : {}) })
  }
  return <LineDialog title={`Remove line — ${name}`} failure={failure} onClose={onClose}
    actions={<Button color="danger" disabled={pending} onClick={remove}>{pending ? "Removing…" : "Remove"}</Button>}>
    {generic ? <FormGroup className="mb-0">
      <Label for="logistics-remove-reason">Explanation <span className="text-danger">*</span> <span className="text-muted">(standard product)</span></Label>
      <Input id="logistics-remove-reason" maxLength={500} value={reason} invalid={missing} placeholder="e.g. club already has diplomas"
        onChange={event => { setReason(event.target.value); setMissing(false) }} />
      <FormFeedback>An explanation is required for a standard product.</FormFeedback>
    </FormGroup> : <p className="mb-0">Remove this product from the sheet?</p>}
  </LineDialog>
}

// Another active product that fits a standard line, such as a new batch of the medals, chosen for this
// sheet. The line keeps it while the event fits it; the quantities set by admin stay, to set again.
export function ChangeProductDialog({ name, alternatives, quantitiesSet, pending, failure, onSubmit, onClose }) {
  const [product, setProduct] = useState(alternatives[0]?.id || "")
  return <LineDialog title={`Change product — ${name}`} failure={failure} onClose={onClose}
    actions={<Button color="primary" disabled={pending || !product} onClick={() => onSubmit({ action: "setProduct", product })}>{pending ? "Saving…" : "Save"}</Button>}>
    <FormGroup className="mb-0">
      <Label for="logistics-line-product">Product</Label>
      <Input id="logistics-line-product" type="select" value={product} onChange={event => setProduct(event.target.value)}>
        {alternatives.map(option => <option key={option.id} value={option.id}>
          {offeredName(`${option.name} · ${supplierNames[option.supplier] || option.supplier}`, option.onSheet)}
        </option>)}
      </Input>
      <FormText>Active products that fit the event, with their catalogue supplier, including those already on another line; a supplier
        chosen for this line stays. The line keeps the product chosen while the event fits it.</FormText>
    </FormGroup>
    {quantitiesSet && <Alert color="warning" className="py-2 mt-3 mb-0">The quantities set by admin stay. Set them again to confirm them for the new product.</Alert>}
  </LineDialog>
}

// "Medals — standard line, follows the event (now Medals Gold)" for a removed generic family; "Medals Gold · already on the sheet" for a
// product a line holds, which comes as a line of its own (D21).
const addableName = entry => entry.generic
  ? `${familyName(entry.family)} — standard line, follows the event (${entry.product ? `now ${entry.product.name}` : "no catalogue product yet"})`
  : offeredName(entry.product.name, entry.onSheet)

export function AddProductDialog({ addable, pending, failure, onSubmit, onClose }) {
  const [line, setLine] = useState(firstOffer(addable)?.key || "")
  const chosen = addable.find(entry => entry.key === line)
  return <LineDialog title="Add product from catalog" failure={failure} onClose={onClose}
    actions={addable.length > 0 && <Button color="primary" disabled={pending || !line} onClick={() => onSubmit({ action: "addLine", line })}>
      {pending ? "Adding…" : "Add"}
    </Button>}>
    {addable.length ? <>
      <FormGroup className="mb-0">
        <Label for="logistics-add-product">Product</Label>
        <Input id="logistics-add-product" type="select" value={line} onChange={event => setLine(event.target.value)}>
          {addable.map(entry => <option key={entry.key} value={entry.key}>{addableName(entry)}</option>)}
        </Input>
        <FormText>Active products for the event's sport, including those already on the sheet. A product already on the sheet comes as a
          separate line, “To complete”: enter its quantity and supplier on the sheet.</FormText>
      </FormGroup>
      {chosen?.onSheet && <Alert color="info" className="py-2 mt-3 mb-0">
        {`New line: ${chosen.product.name} · To complete · supplier ${supplierNames[chosen.supplier] || chosen.supplier} until you change it.`}
      </Alert>}
    </> : <p className="mb-0">The catalogue has no active product for the event's sport.</p>}
  </LineDialog>
}
